import { FC } from "react";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { ChatBubbleIcon, InfoCircledIcon, PaperPlaneIcon } from "@radix-ui/react-icons";
import { useChatDialog } from "./ChatDialog.hooks";
import { ScrollArea } from "@/components/ui/scroll-area";
import RectTailRight from "@/assets/images/rect_tail_right.svg";
import { AlertMessageDialog, ConfirmMessageDialog } from "@/components/MessageDialog";
import { FilterIcon, HistoryIcon } from "lucide-react";
import { ChatHistory } from "./ChatHistory";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useSearchPage } from "../SearchPage.hooks";
import { FilterView } from "../FilterView";
import { ChatAssistantMessage } from "@/components/Chat/ChatAssistantMessage";

type ChatDialogProps = Pick<
  ReturnType<typeof useSearchPage>,
  | "chatViewState"
  | "openChatDialog"
  | "updateOpenChatDialog"
  | "form"
  | "onSubmit"
  | "filterModelGroups"
  | "filterLanguages"
  | "filterCategories"
  | "onClickFilterClear"
  | "onClickOpenSearchModel"
>;

export const ChatDialog: FC<ChatDialogProps> = (props) => {
  const { chatViewState, openChatDialog, updateOpenChatDialog, form, onClickOpenSearchModel } = props;
  const {
    t,
    onKeyDown,
    scrollAnchorRef,
    onSubmit,
    messages,
    isLoading,
    input,
    setInput,
    apiErrorMessage,
    apiErrorDialogOpen,
    setApiErrorDialogOpen,
    recentRallyLimit,
    inputPlaceholder,
    textAreaRef,
    chatHistoryOpen,
    setChatHistoryOpen,
    onClickHistory,
    histories,
    onClickHistoryItem,
    onClickHistoryTrash,
    chatTitle,
    deleteHistory,
    deleteConfirmOpen,
    setDeleteConfirmOpen,
    callDeleteChat,
    onClickNew,
    notFoundError,
    openFilterOnChat,
    updateOpenFilterOnChat,
  } = useChatDialog(chatViewState, form);

  const chatHistoryView = (
    <div className="flex flex-col">
      <ChatHistory
        selectedId={chatViewState.chatId}
        open={chatHistoryOpen}
        setOpen={setChatHistoryOpen}
        histories={histories}
        onClickMenuItem={onClickHistoryItem}
        onClickTrash={onClickHistoryTrash}
      />
      <Button variant="ghost" className="size-10 p-0" onClick={onClickHistory}>
        <HistoryIcon className="h-4 w-4" />
      </Button>
    </div>
  );

  const filterButtonDialogView = (
    <div className="flex flex-col">
      <Dialog open={openFilterOnChat} onOpenChange={updateOpenFilterOnChat}>
        <DialogContent
          className="top-[calc(50svh+10px)] w-[288px] max-w-[100vw] rounded-none border-none p-0 sm:left-[316px] sm:top-[calc(50svh-10px)] sm:h-[calc(100svh-56px)] sm:w-[288px] sm:max-w-[calc(100vw-56px)] sm:rounded-none sm:border-none"
          showCloseButton={false}
        >
          <FilterView
            showSubmitButton={false}
            showCloseButton={true}
            onClickCloseButton={() => updateOpenFilterOnChat(false)}
            formScrollAreaClassName="h-[calc(100svh-133px)] sm:h-[calc(100svh-171px)]"
            onClickReferenceButton={onClickOpenSearchModel}
            {...props}
          />
        </DialogContent>
      </Dialog>
      <Button variant="ghost" className="size-10 p-0" onClick={() => updateOpenFilterOnChat(!openFilterOnChat)}>
        <FilterIcon className="h-4 w-4" />
      </Button>
    </div>
  );

  return (
    <>
      <Dialog open={openChatDialog} onOpenChange={updateOpenChatDialog}>
        <DialogContent
          className="fixed left-[50vw] top-[calc(50svh+10px)] flex h-[calc(100svh-16px)] w-[100vw] max-w-[100vw] flex-col gap-0 rounded-lg p-4 pb-0 sm:left-[316px] sm:top-[calc(50svh-10px)] sm:h-[calc(100svh-56px)] sm:w-[552px] sm:max-w-[calc(100vw-56px)]"
          onAnimationEnd={(e) => {
            if (e.animationName === "enter") {
              // モバイルでソフトウェアキーボードを表示するため、表示アニメーション終了後にフォーカスする。
              textAreaRef.current?.focus();
            }
          }}
        >
          <DialogHeader className="h-6 flex-1 pb-8 pr-8">
            <div className="flex flex-row">
              <p className="overflow-hidden text-ellipsis whitespace-nowrap text-left text-base">{chatTitle}</p>
              {recentRallyLimit && (
                <div className="mx-2 flex items-center rounded-sm bg-gray-100 px-1">
                  <p className="text-xs text-gray-600">{t("chat_input_memorie_count", { n: recentRallyLimit })}</p>
                </div>
              )}
            </div>
          </DialogHeader>

          {notFoundError && (
            <Alert>
              <InfoCircledIcon className="h-4 w-4" />
              <AlertTitle>{t("chat_none")}</AlertTitle>
              <AlertDescription>{t("chat_none_message")}</AlertDescription>
            </Alert>
          )}

          {!notFoundError && (
            <ScrollArea className="-mx-3 px-1">
              {/* NOTE: scrollArea 内部のdisplay table指定によって、Child要素の文言が長くなると、幅がChildの要素の幅になるので、以下の指定をWrapperして回避 */}
              <div className="table w-full table-fixed">
                <div className="flex flex-col px-2 pb-4">
                  <ChatAssistantMessage type="first" firstMessage={t("chat_first_message")} />

                  {messages.map((message, index) => {
                    if (message.role === "user") {
                      return (
                        <div key={index} className="flex items-end justify-end pb-4">
                          <div className="max-w-[calc(100%-80px)] rounded-md bg-[#eee] p-2">
                            <p className="whitespace-pre-line break-words text-sm text-gray-600">{message.content}</p>
                          </div>
                          <img src={RectTailRight} className="mb-2 h-2 w-2" />
                        </div>
                      );
                    } else {
                      return <ChatAssistantMessage key={index} type="normal" message={message} />;
                    }
                  })}

                  {isLoading && messages[messages.length - 1].role === "user" && (
                    <ChatAssistantMessage type="loading" />
                  )}
                </div>
              </div>
              <div ref={scrollAnchorRef} />
            </ScrollArea>
          )}

          {chatViewState.viewType === "new" && (
            <div className="flex flex-col">
              {/* iphoneでチャット画面表示時に、
              画面下部前面にソフトウェアキーボードが表示されて Textarea が隠れる現象があるため、
              tabIndex={0} を設定してフォーカスを別のコンポーネントに当てて回避する */}
              <form
                tabIndex={0}
                className="rounded-md border"
                onSubmit={(e) => {
                  e.preventDefault();
                  onSubmit();
                }}
              >
                <Textarea
                  className="h-[112px] min-h-0 resize-none border-0 border-transparent px-3 py-[10px] pt-2 text-sm focus-visible:ring-0 focus-visible:ring-offset-0"
                  onKeyDown={onKeyDown}
                  placeholder={inputPlaceholder}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  ref={textAreaRef}
                />

                <div className="flex flex-row gap-x-4">
                  {chatHistoryView}
                  {filterButtonDialogView}

                  <div className="flex-1" />

                  <Button variant="ghost" className="size-10 p-0" disabled={isLoading}>
                    <PaperPlaneIcon className="h-4 w-4" />
                  </Button>
                </div>
              </form>

              <div className="mr-2 mt-0.5 text-right text-[10px]">{t("chat_send_shortcut")}</div>
            </div>
          )}

          {chatViewState.viewType === "load" && (
            <div tabIndex={0} className="mb-4 flex flex-row rounded-md border">
              {chatHistoryView}

              <div className="flex-1" />

              <Button variant="ghost" className="gap-x-2 px-3 py-0" onClick={onClickNew}>
                <ChatBubbleIcon className="h-4 w-4" />
                <p className="text-sm">{t("chat_new")}</p>
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmMessageDialog
        open={deleteConfirmOpen}
        setOpen={setDeleteConfirmOpen}
        title={t("delete_title", { title: deleteHistory?.title ?? "" })}
        message={t("delete_message")}
        actionButtonLabel={t("delete")}
        actionButtonBg="bg-destructive"
        onClickAction={callDeleteChat}
        cancelButtonLabel={t("cancel")}
        contentClassName="sm:left-[316px]"
      />
      <AlertMessageDialog open={apiErrorDialogOpen} message={apiErrorMessage} setOpen={setApiErrorDialogOpen} />
    </>
  );
};
